import { add, isWithinInterval, sub } from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import { interval } from 'rxjs';

export type EventStatus =
  | 'COMPLETE'
  | 'IN_PLAY'
  | 'GOING_IN_PLAY'
  | 'SOON_IN_PLAY'
  | 'SCHEDULED'
  | 'SCHEDULED_NO_IN_PLAY';

const isWithinDurationOfEvent = (startTime: string, duration: Duration) =>
  isWithinInterval(Date.now(), {
    start: sub(new Date(startTime), duration),
    end: add(new Date(startTime), { days: 1 })
  });

export interface Props {
  inPlay?: boolean;
  canTurnInPlay?: boolean;
  startTime: string;
  eventStatusOverride?: EventStatus;
}

const getEventStatus = ({
  canTurnInPlay = false,
  inPlay = false,
  startTime
}: Props): EventStatus => {
  if (!canTurnInPlay || !startTime) return 'SCHEDULED_NO_IN_PLAY';

  if (inPlay) return 'IN_PLAY';

  if (isWithinDurationOfEvent(startTime, { seconds: 0 })) {
    return 'GOING_IN_PLAY';
  }

  if (isWithinDurationOfEvent(startTime, { minutes: 5 })) {
    return 'SOON_IN_PLAY';
  }

  return 'SCHEDULED';
};

const useEventStatus = ({ inPlay, startTime, canTurnInPlay, eventStatusOverride }: Props) => {
  const [eventStatus, setEventStatus] = useState(
    () => eventStatusOverride ?? getEventStatus({ inPlay, canTurnInPlay, startTime })
  );
  const hasBeenInPlay = useRef(inPlay);

  useEffect(() => {
    if (!hasBeenInPlay.current && inPlay) {
      hasBeenInPlay.current = true;
    }
  }, [inPlay]);

  useEffect(() => {
    if (eventStatusOverride) {
      setEventStatus(eventStatusOverride);
      return;
    }

    if (hasBeenInPlay.current && !inPlay) {
      setEventStatus('COMPLETE');
      return;
    }

    const status = getEventStatus({ inPlay, canTurnInPlay, startTime });

    if (['IN_PLAY', 'SCHEDULED_NO_IN_PLAY', 'COMPLETE'].includes(status)) {
      setEventStatus(status);
      return;
    }

    const subscription = interval(1000).subscribe(() => {
      setEventStatus(getEventStatus({ inPlay, startTime, canTurnInPlay }));
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [canTurnInPlay, eventStatusOverride, inPlay, startTime]);

  return eventStatus;
};

export default useEventStatus;
